"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
    Shield,
    ArrowLeft,
    ArrowRight,
    Check,
    Upload,
    FileText,
    User,
    ClipboardCheck,
    CheckCircle2,
    X
} from "lucide-react";
import { mockCategories } from "@/lib/mock-data";

type Step = 1 | 2 | 3;

export default function SubmitComplaintPage() {
    const [currentStep, setCurrentStep] = useState<Step>(1);
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [files, setFiles] = useState<string[]>([]);

    // Form state
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        categoryId: "",
        subject: "",
        description: "",
        accusedInfo: "",
        incidentDate: "",
        incidentLocation: "",
        consentPdpa: false,
    });

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files).map(f => f.name);
            setFiles(prev => [...prev, ...newFiles].slice(0, 5));
        }
    };

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = () => {
        // Simulate submission
        setShowSuccess(true);
    };

    const steps = [
        { num: 1, title: "ข้อมูลผู้ร้อง", icon: User },
        { num: 2, title: "รายละเอียดเรื่อง", icon: FileText },
        { num: 3, title: "ยืนยันและส่ง", icon: ClipboardCheck },
    ];

    return (
        <div className="min-h-screen bg-muted/30">
            {/* Header */}
            <header className="sticky top-0 z-50 w-full border-b bg-white">
                <div className="container mx-auto flex h-16 items-center justify-between px-4">
                    <div className="flex items-center gap-3">
                        <Image
                            src="/logo_cus.png"
                            alt="กรมศุลกากร"
                            width={48}
                            height={48}
                            className="h-12 w-12 object-contain"
                        />
                        <div className="hidden sm:block">
                            <p className="text-sm font-semibold text-primary">กรมศุลกากร</p>
                            <p className="text-xs text-muted-foreground">Thai Customs Department</p>
                        </div>
                    </div>
                    <Button variant="ghost" asChild>
                        <Link href="/">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            กลับหน้าหลัก
                        </Link>
                    </Button>
                </div>
            </header>

            <main className="container mx-auto px-4 py-8">
                <div className="max-w-3xl mx-auto">
                    {/* Page Title */}
                    <div className="text-center mb-8">
                        <h1 className="heading-md text-primary mb-2">ยื่นเรื่องร้องเรียน</h1>
                        <p className="text-muted-foreground">กรอกข้อมูลให้ครบถ้วนเพื่อดำเนินการยื่นเรื่องร้องเรียน</p>
                    </div>

                    {/* Step Indicator */}
                    <div className="flex justify-between mb-8 relative">
                        <div className="absolute top-5 left-0 right-0 h-0.5 bg-border" />
                        {steps.map((step) => (
                            <div key={step.num} className="relative flex flex-col items-center z-10">
                                <div
                                    className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${currentStep >= step.num
                                        ? "bg-primary text-white"
                                        : "bg-muted text-muted-foreground"
                                        }`}
                                >
                                    {currentStep > step.num ? (
                                        <Check className="h-5 w-5" />
                                    ) : (
                                        <step.icon className="h-5 w-5" />
                                    )}
                                </div>
                                <span className={`text-sm mt-2 ${currentStep >= step.num ? "text-primary font-medium" : "text-muted-foreground"}`}>
                                    {step.title}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Form Steps */}
                    <Card>
                        {/* Step 1: Complainant Info */}
                        {currentStep === 1 && (
                            <>
                                <CardHeader>
                                    <CardTitle>ข้อมูลผู้ร้องเรียน</CardTitle>
                                    <CardDescription>
                                        กรอกข้อมูลของท่านเพื่อใช้ในการติดต่อกลับ หรือเลือกร้องเรียนแบบไม่เปิดเผยตัวตน
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Anonymous Toggle */}
                                    <div className="flex items-center gap-3 p-4 rounded-lg border bg-muted/50">
                                        <input
                                            type="checkbox"
                                            id="anonymous"
                                            checked={isAnonymous}
                                            onChange={(e) => setIsAnonymous(e.target.checked)}
                                            className="h-5 w-5 rounded border-gray-300"
                                        />
                                        <div>
                                            <Label htmlFor="anonymous" className="font-medium">ร้องเรียนแบบไม่เปิดเผยตัวตน</Label>
                                            <p className="text-sm text-muted-foreground">ข้อมูลของท่านจะไม่ถูกเปิดเผยต่อผู้ถูกร้องเรียน</p>
                                        </div>
                                    </div>

                                    {!isAnonymous && (
                                        <>
                                            <div className="space-y-2">
                                                <Label htmlFor="name">ชื่อ-นามสกุล / ชื่อบริษัท <span className="text-destructive">*</span></Label>
                                                <Input
                                                    id="name"
                                                    placeholder="กรอกชื่อ-นามสกุล หรือชื่อบริษัท"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                                />
                                            </div>
                                            <div className="grid sm:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="email">อีเมล <span className="text-destructive">*</span></Label>
                                                    <Input
                                                        id="email"
                                                        type="email"
                                                        placeholder="example@email.com"
                                                        value={formData.email}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="phone">เบอร์โทรศัพท์</Label>
                                                    <Input
                                                        id="phone"
                                                        placeholder="08X-XXX-XXXX"
                                                        value={formData.phone}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </CardContent>
                            </>
                        )}

                        {/* Step 2: Complaint Details */}
                        {currentStep === 2 && (
                            <>
                                <CardHeader>
                                    <CardTitle>รายละเอียดเรื่องร้องเรียน</CardTitle>
                                    <CardDescription>
                                        กรอกรายละเอียดของเรื่องที่ต้องการร้องเรียนให้ครบถ้วน
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <div className="space-y-2">
                                        <Label>ประเภทเรื่องร้องเรียน <span className="text-destructive">*</span></Label>
                                        <Select
                                            value={formData.categoryId}
                                            onValueChange={(value) => setFormData(prev => ({ ...prev, categoryId: value }))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="เลือกประเภทเรื่องร้องเรียน" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {mockCategories.map((cat) => (
                                                    <SelectItem key={cat.id} value={cat.id}>
                                                        {cat.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="subject">เรื่อง <span className="text-destructive">*</span></Label>
                                        <Input
                                            id="subject"
                                            placeholder="หัวข้อเรื่องร้องเรียน"
                                            value={formData.subject}
                                            onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="description">รายละเอียด <span className="text-destructive">*</span></Label>
                                        <Textarea
                                            id="description"
                                            placeholder="อธิบายรายละเอียดของเรื่องร้องเรียน..."
                                            rows={5}
                                            value={formData.description}
                                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="accusedInfo">ข้อมูลผู้ถูกร้องเรียน (ถ้าทราบ)</Label>
                                        <Input
                                            id="accusedInfo"
                                            placeholder="ชื่อ ตำแหน่ง หรือลักษณะเฉพาะ"
                                            value={formData.accusedInfo}
                                            onChange={(e) => setFormData(prev => ({ ...prev, accusedInfo: e.target.value }))}
                                        />
                                    </div>

                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="incidentDate">วันที่เกิดเหตุ</Label>
                                            <Input
                                                id="incidentDate"
                                                type="date"
                                                value={formData.incidentDate}
                                                onChange={(e) => setFormData(prev => ({ ...prev, incidentDate: e.target.value }))}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="incidentLocation">สถานที่เกิดเหตุ</Label>
                                            <Input
                                                id="incidentLocation"
                                                placeholder="ด่านศุลกากร สำนักงาน ฯลฯ"
                                                value={formData.incidentLocation}
                                                onChange={(e) => setFormData(prev => ({ ...prev, incidentLocation: e.target.value }))}
                                            />
                                        </div>
                                    </div>

                                    {/* File Upload */}
                                    <div className="space-y-2">
                                        <Label>แนบหลักฐาน (ถ้ามี)</Label>
                                        <div className="border-2 border-dashed rounded-lg p-6 text-center">
                                            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                                            <p className="text-sm text-muted-foreground mb-2">
                                                ลากไฟล์มาวางหรือคลิกเพื่อเลือกไฟล์
                                            </p>
                                            <p className="text-xs text-muted-foreground mb-4">
                                                PDF, JPG, PNG, DOC, DOCX (สูงสุด 10MB ต่อไฟล์, 5 ไฟล์)
                                            </p>
                                            <Input
                                                type="file"
                                                className="hidden"
                                                id="file-upload"
                                                multiple
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                onChange={handleFileChange}
                                            />
                                            <Button variant="outline" asChild>
                                                <label htmlFor="file-upload" className="cursor-pointer">
                                                    เลือกไฟล์
                                                </label>
                                            </Button>
                                        </div>
                                        {files.length > 0 && (
                                            <div className="space-y-2 mt-4">
                                                {files.map((file, index) => (
                                                    <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                                                        <div className="flex items-center gap-2">
                                                            <FileText className="h-4 w-4" />
                                                            <span className="text-sm">{file}</span>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removeFile(index)}
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </>
                        )}

                        {/* Step 3: Review & Submit */}
                        {currentStep === 3 && (
                            <>
                                <CardHeader>
                                    <CardTitle>ตรวจสอบและยืนยันข้อมูล</CardTitle>
                                    <CardDescription>
                                        กรุณาตรวจสอบข้อมูลให้ถูกต้องก่อนส่งเรื่องร้องเรียน
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Summary */}
                                    <div className="space-y-4">
                                        <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                                            <h4 className="font-medium">ข้อมูลผู้ร้อง</h4>
                                            {isAnonymous ? (
                                                <Badge variant="secondary">ไม่เปิดเผยตัวตน</Badge>
                                            ) : (
                                                <div className="text-sm space-y-1">
                                                    <p><span className="text-muted-foreground">ชื่อ:</span> {formData.name || "-"}</p>
                                                    <p><span className="text-muted-foreground">อีเมล:</span> {formData.email || "-"}</p>
                                                    <p><span className="text-muted-foreground">เบอร์โทร:</span> {formData.phone || "-"}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                                            <h4 className="font-medium">รายละเอียดเรื่องร้องเรียน</h4>
                                            <div className="text-sm space-y-1">
                                                <p>
                                                    <span className="text-muted-foreground">ประเภท:</span>{" "}
                                                    {mockCategories.find(c => c.id === formData.categoryId)?.name || "-"}
                                                </p>
                                                <p><span className="text-muted-foreground">เรื่อง:</span> {formData.subject || "-"}</p>
                                                <p><span className="text-muted-foreground">รายละเอียด:</span></p>
                                                <p className="whitespace-pre-wrap text-muted-foreground">{formData.description || "-"}</p>
                                                {formData.incidentDate && (
                                                    <p><span className="text-muted-foreground">วันที่เกิดเหตุ:</span> {formData.incidentDate}</p>
                                                )}
                                                {formData.incidentLocation && (
                                                    <p><span className="text-muted-foreground">สถานที่:</span> {formData.incidentLocation}</p>
                                                )}
                                                {files.length > 0 && (
                                                    <p><span className="text-muted-foreground">ไฟล์แนบ:</span> {files.length} ไฟล์</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* PDPA Consent */}
                                    <div className="flex items-start gap-3 p-4 rounded-lg border bg-muted/50">
                                        <input
                                            type="checkbox"
                                            id="pdpa"
                                            checked={formData.consentPdpa}
                                            onChange={(e) => setFormData(prev => ({ ...prev, consentPdpa: e.target.checked }))}
                                            className="h-5 w-5 rounded border-gray-300 mt-0.5"
                                        />
                                        <div>
                                            <Label htmlFor="pdpa" className="font-medium">
                                                ยินยอมให้เก็บรวบรวมและใช้ข้อมูลส่วนบุคคล <span className="text-destructive">*</span>
                                            </Label>
                                            <p className="text-sm text-muted-foreground">
                                                ข้าพเจ้ายินยอมให้กรมศุลกากรเก็บรวบรวม ใช้ และเปิดเผยข้อมูลส่วนบุคคลของข้าพเจ้า
                                                เพื่อวัตถุประสงค์ในการดำเนินการตามเรื่องร้องเรียน ตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </>
                        )}

                        {/* Navigation Buttons */}
                        <div className="flex justify-between p-6 pt-0">
                            <Button
                                variant="outline"
                                onClick={() => setCurrentStep((prev) => (prev - 1) as Step)}
                                disabled={currentStep === 1}
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                ย้อนกลับ
                            </Button>

                            {currentStep < 3 ? (
                                <Button
                                    onClick={() => setCurrentStep((prev) => (prev + 1) as Step)}
                                >
                                    ถัดไป
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!formData.consentPdpa}
                                >
                                    <Check className="mr-2 h-4 w-4" />
                                    ส่งเรื่องร้องเรียน
                                </Button>
                            )}
                        </div>
                    </Card>
                </div>
            </main>

            {/* Success Dialog */}
            <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader className="text-center">
                        <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle2 className="h-10 w-10 text-green-600" />
                        </div>
                        <DialogTitle className="text-xl">ยื่นเรื่องร้องเรียนสำเร็จ</DialogTitle>
                        <DialogDescription>
                            เรื่องร้องเรียนของท่านได้รับการบันทึกเรียบร้อยแล้ว
                        </DialogDescription>
                    </DialogHeader>
                    <div className="text-center space-y-4">
                        <div className="p-4 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">หมายเลขอ้างอิง</p>
                            <p className="text-2xl font-bold text-primary">CMP-256901-0007</p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            กรุณาจดหมายเลขอ้างอิงนี้ไว้เพื่อใช้ติดตามสถานะ
                            {!isAnonymous && " เราจะส่งอีเมลยืนยันไปยังที่อยู่อีเมลที่ท่านระบุ"}
                        </p>
                        <div className="flex gap-3">
                            <Button variant="outline" asChild className="flex-1">
                                <Link href="/">กลับหน้าหลัก</Link>
                            </Button>
                            <Button asChild className="flex-1">
                                <Link href="/track">ติดตามสถานะ</Link>
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
